import { Component, Input, OnInit } from '@angular/core';
import { creditBillingBodyData, creditCeilingBodyData, EnumCardVerificationForm, EnumCardVerificationSection, saleTargetBodyData } from '../distributor-data/distributor-data.data';
import { BehaviorSubject } from 'rxjs';
import { ICardVerificationForm, ICreditCeilingStep, INoteChangeVerification, IPayloadRegistrationVerification } from '../../../../interfaces/detail-registration.interface';
import { DetailRegistrationDistributorService } from '../detail-registration.service';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { DistributorTabStatus } from '@config/enum/distributor.enum';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';

@Component({
  selector: 'app-credit-ceiling',
  templateUrl: './credit-ceiling.component.html',
  styleUrls: ['./credit-ceiling.component.scss'],
})
export class CreditCeilingComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() isViewOnly = false;

  bodyDataCredit: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataSaleTarget: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataBilling: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);

  dataValue: BehaviorSubject<ICreditCeilingStep> = new BehaviorSubject<ICreditCeilingStep>({} as ICreditCeilingStep);
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  revisionNoteData: IPayloadRegistrationVerification;
  snapshotParams!: Params;
  revisionType: EnumDistributorRevisionSession;

  get isNeedValidation() {
    return this.snapshotParams.status !== DistributorTabStatus.REGISTERED && !this.isViewOnly;
  }

  get ShowBadge() {
    return this.detailRegistrationService.showBadgePendingDetail();
  }

  constructor(public detailRegistrationService: DetailRegistrationDistributorService, private activatedRoute: ActivatedRoute) {
    this.snapshotParams = this.activatedRoute.snapshot.params;
    if (this.snapshotParams.status === DistributorTabStatus.REGISTERED) {
      this.isViewOnly = true;
    }
    const statusTab = this.activatedRoute.snapshot.params.tab;
    this.revisionType = this.detailRegistrationService.getTypeRevision(statusTab);
  }

  ngOnInit(): void {
    this.getRevisionNoteData();
    this.getData();
  }

  getRevisionNoteData() {
    this.revisionNoteData = this.detailRegistrationService.getSessionRevisionNote(this.revisionType) ?? ({} as IPayloadRegistrationVerification);
  }

  getData() {
    this.detailRegistrationService.getCreditCeilingStep().subscribe((value) => {
      if (!value) return;
      this.dataValue.next(value);
      !this.isViewOnly && this.handleValidationForm();
      this.generateBodyData(value);
      this.isLoading.next(false);
    });
  }

  generateBodyData(data: ICreditCeilingStep) {
    const { credit_ceiling_granted, sale_target, credit_billing } = data;

    const _creditCeilingBody: ICardVerificationForm[] = creditCeilingBodyData(credit_ceiling_granted.current_data);
    const _creditCeiling = this.detailRegistrationService.generateBodyData(credit_ceiling_granted, _creditCeilingBody);
    this.bodyDataCredit.next(_creditCeiling);

    const _saleTargetBody: ICardVerificationForm[] = saleTargetBodyData(sale_target.current_data);
    const _saleTarget = this.detailRegistrationService.generateBodyData(sale_target, _saleTargetBody);
    this.bodyDataSaleTarget.next(_saleTarget);

    const _billingBody: ICardVerificationForm[] = creditBillingBodyData(credit_billing.current_data);
    const _billing = this.detailRegistrationService.generateBodyData(credit_billing, _billingBody);
    this.bodyDataBilling.next(_billing);
  }

  noteChanged(e: INoteChangeVerification) {
    this.detailRegistrationService.setRevisionData(this.revisionNoteData, e, this.revisionType);
  }

  noteValue(type: EnumCardVerificationSection) {
    if (type === EnumCardVerificationSection.DELIVERY_ADDRESS) {
      return [];
    } else {
      const { revision_note } = this.revisionNoteData;
      if (!revision_note) return [];
      const value = revision_note[type.toLowerCase() as keyof typeof revision_note];
      this.handleValidationForm();
      return value;
    }
  }

  handleValidationForm() {
    if (!this.isNeedValidation) return;
    const { credit_ceiling_granted, sale_target, credit_billing } = this.dataValue.value;
    const { revision_note } = this.revisionNoteData;

    const creditCeilingControl = this.form.get('credit_ceiling_granted');
    const saleTargetControl = this.form.get('sale_target');
    const creditBillingControl = this.form.get('credit_billing');

    if (creditCeilingControl) {
      if (
        this.detailRegistrationService.checkValidationFormStepper(creditCeilingControl, credit_ceiling_granted) &&
        this.detailRegistrationService.isVerifiedAndRevision(credit_ceiling_granted)
      ) {
        creditCeilingControl.clearValidators();
      } else {
        creditCeilingControl.setValidators([Validators.required]);
        creditCeilingControl.setValue(revision_note && revision_note.credit_ceiling_granted);
      }
      creditCeilingControl.updateValueAndValidity();
    }

    if (saleTargetControl) {
      if (this.detailRegistrationService.checkValidationFormStepper(saleTargetControl, sale_target) && this.detailRegistrationService.isVerifiedAndRevision(sale_target)) {
        saleTargetControl.clearValidators();
      } else {
        saleTargetControl.setValidators([Validators.required]);
        saleTargetControl.setValue(revision_note && revision_note.sale_target);
      }
      saleTargetControl.updateValueAndValidity();
    }

    if (creditBillingControl) {
      if (this.detailRegistrationService.checkValidationFormStepper(creditBillingControl, credit_billing) && this.detailRegistrationService.isVerifiedAndRevision(credit_billing)) {
        creditBillingControl.clearValidators();
      } else {
        creditBillingControl.setValidators([Validators.required]);
        creditBillingControl.setValue(revision_note && revision_note.credit_billing);
      }
      creditBillingControl.updateValueAndValidity();
    }
  }

  afterActionVerification(event: string | null) {
    if (event === null) {
      this.getRevisionNoteData();
    }
    setTimeout(() => {
      this.getData();
    }, 500);
  }

  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
}
